import React, { useState, useEffect } from "react";
import {
  Card,
  Headline,
  Paragraph,
  Button,
  Checkbox,
  Loading,
} from "_styleguide";
import { useNavigate } from "react-router-dom";
import { useSettings } from "_contexts/useSettings";
import EquifaxLogo from "_assets/logos/equifax.svg";
import PullingCredit from "./_components/PullingCredit/PullingCredit";
import VerifySSN from "./_components/VerifySSN/VerifySSN";
import { hexToRgba } from "_assets/js/helpers";
import { useLead } from "_contexts/useLead";
import axios from "axios";
import { baseApi } from "_helpers/api";
import { message } from "@centrate-io/barn";
import { useApolloClient } from "@apollo/client";
import { getCreditState } from "_graphql/queries";
import { isDarkColor } from "_helpers/colors";
import { useStats } from "_contexts/useStats";
import { IconArrowDown } from "@tabler/icons-react";
import { useGTM } from "_hooks/useGTM";
import "./CreditReport.scss";

function CreditReport(props) {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { lead, reload } = useLead();
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [stateData, setStateData] = useState();
  const { settings, isPreview } = useSettings();
  const { updateStats } = useStats();
  const { gtmEvent, gtmError } = useGTM();
  const pageSettings = props.settings?.settings;

  const secondaryRgba = hexToRgba(settings?.secondary_color || "#000", 0.6);
  const primaryColor = { color: settings?.primary_color || "#0f766e" };

  const runCreditReport = async () => {
    if (isPreview) {
      message.success("Preview mode, skipping credit report");
      return;
    }
    try {
      setLoading(true);
      gtmEvent("credit_report_started");
      localStorage.setItem(
        "pp_cr_attempts",
        (localStorage.getItem("pp_cr_attempts") || 0) + 1
      );
      await axios.post(`${baseApi()}/credit-report`, {
        id: lead.id,
      });
      await checkState();
    } catch (err) {
      gtmError("credit_report_failed");
      if (localStorage.getItem("pp_cr_attempts") >= 2) {
        navigate(`/contact-us`);
        return;
      }
      message.error(
        "Report not found, please check your information and try again"
      );
      setLoading(false);
    }
  };

  const checkState = async () => {
    const stateResponse = await client.query({
      query: getCreditState,
      variables: {
        application_id: lead.id,
      },
    });
    const newState = stateResponse?.data?.getCreditState;
    if (newState?.state === "select" || newState?.state === "verify") {
      await updateStats({
        application_id: lead.id,
        self_enrollment_id: settings.id,
        page: props?.settings?.name,
        drop_off_page: props?.settings?.next || props?.settings?.name,
      });
    }
    if (newState?.state === "select" && props?.settings?.next) {
      await reload();
      navigate(`/${props?.settings?.next}`);
    }
    setStateData(newState || undefined);
    setInitialLoading(false);
    setLoading(false);
  };

  useEffect(() => {
    checkState();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (initialLoading) {
    return <Loading />;
  } else if (loading) {
    return <PullingCredit />;
  }

  if (stateData?.state === "verify") {
    return (
      <VerifySSN
        ssns={stateData?.ssns || []}
        lead={lead}
        checkState={checkState}
        setLoadingReport={setLoading}
        pageSettings={pageSettings}
      />
    );
  }

  const primaryCheckboxColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryCheckboxColor) ? "#fff" : "#000";
  const primaryOpacity = hexToRgba(primaryCheckboxColor, 0.2);

  return loading ? (
    <PullingCredit />
  ) : (
    <div id="credit_report">
      <Card>
        <div className="apply-form">
          <Headline>{pageSettings.title}</Headline>
          {pageSettings?.subtitle ? (
            <Paragraph
              opacity={0.6}
              align="left"
              size="14px"
              dangerouslySetInnerHTML={{
                __html: pageSettings?.subtitle,
              }}
            />
          ) : null}
        </div>
      </Card>
      <div className="credit-report-actions">
        <div
          className="cr-checkbox-container"
          style={{
            "--cr-primary-opacity": primaryOpacity,
            "--cr-primary-color": primaryCheckboxColor,
          }}
        >
          {!agreed ? (
            <div className="cr-checkbox-label">
              <IconArrowDown />
              <span className="cr-checkbox-label-mobile">
                Tap here to authorize
              </span>
              <span className="cr-checkbox-label-desktop">
                Click here to authorize
              </span>
            </div>
          ) : null}
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            size="large"
            style={{
              "--checkbox-color": primaryCheckboxColor,
              "--checkbox-font-color": primaryFontColor,
            }}
          >
            {pageSettings?.agreement_content ? (
              <span
                className="cr-agreement-content"
                dangerouslySetInnerHTML={{
                  __html: pageSettings?.agreement_content,
                }}
              />
            ) : (
              <span style={{ color: secondaryRgba }}>
                I authorize JG Wentworth to verify my credit. I've also read and
                agreed to JG Wentworth's{" "}
                <a href="/" style={primaryColor}>
                  Terms of Use
                </a>{" "}
                &{" "}
                <a href="/" style={primaryColor}>
                  Privacy Policy
                </a>{" "}
                and consent to a credit inquiry and credit verification.
              </span>
            )}
          </Checkbox>
        </div>
        <div className="bureau-disclaimer" style={{ color: secondaryRgba }}>
          Brought to you by <img src={EquifaxLogo} alt="" />
        </div>
        <Button
          block
          size="large"
          style={{
            background: settings?.primary_color || "#0f766e",
            color: primaryFontColor,
          }}
          onClick={runCreditReport}
          disabled={!agreed}
          loading={loading}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

export default CreditReport;
